import { contactSecondary, resolvePrimaryCta, resolvesToPhoneUrl } from "../../lib/cta-defaults";
import { formatPhone, phoneHref } from "@servicepro/utils";
import { CTA } from './cta';
import { CTAFields } from "./cta.fields";
import type { CTAProps } from './cta';
import type { ComponentConfig } from "@puckeditor/core";
import { IconPicker } from "../editor/IconPicker";
import { ImagePicker } from "../editor/ImagePicker";
import type { JobsiteBusinessContext } from '../../types/runtime-config';
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { getWindowJobsiteConfig } from '../../types/runtime-config';
import { resolveBusinessCtaText } from "../../lib/business-bound-prop";
import { withBusinessCtaOverride } from "../../lib/business-bound-fields";

/** What a CTA reads from Puck's metadata: the business, this page, the site's pages, and whether it is one page. */
interface CtaMetadata {
  business?: JobsiteBusinessContext;
  onePage?: boolean;
  pagePath?: string;
  pagePaths?: string[];
}

const CTAConfig: ComponentConfig<CTAProps> = {
  defaultProps: {
    backgroundImage: "", // No default - will use solid background color
    ctaText: "Get Started Today",
    ctaUrl: "/contact",
    headline: "Ready to Experience the Difference?",
    imagePosition: "center",
    overlay: "dark",
    overlayDarkness: 50,
    secondaryCtaText: "Call Now",
    secondaryCtaUrl: "tel:",
    showTrustSignals: "yes",
    subheadline:
      "Join thousands of satisfied customers who trust us for quality service and reliable results.",
    trustSignals: [
      { icon: "CheckCircle", text: "Free Consultation" },
      { icon: "Star", text: "Top Rated Service" },
      { icon: "Calendar", text: "Quick Response Time" },
      { icon: "Shield", text: "Licensed & Insured" },
    ],
    variant: "band",
  },
  fields: {
    headline: {
      ...CTAFields.headline,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.cta.headline"
          label="Headline"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheadline: {
      ...CTAFields.subheadline,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.cta.subheadline"
          label="Subheadline (optional)"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    ctaText: {
      ...CTAFields.ctaText,
      contentEditable: true,
    },
    ctaUrl: CTAFields.ctaUrl,
    backgroundImage: {
      ...CTAFields.backgroundImage,
      render: ({
        value,
        onChange,
      }: {
        value: string | undefined;
        onChange: (val: string | undefined) => void;
      }) => <ImagePicker value={value || ""} onChange={onChange} label="Background Image" />,
    },
    imagePosition: CTAFields.imagePosition,
    overlay: CTAFields.overlay,
    overlayDarkness: CTAFields.overlayDarkness,
    variant: CTAFields.variant,
    showTrustSignals: CTAFields.showTrustSignals,
    trustSignals: {
      ...CTAFields.trustSignals,
      arrayFields: {
        text: CTAFields.trustSignals.arrayFields.text,
        icon: {
          ...CTAFields.trustSignals.arrayFields.icon,
          render: ({
            value,
            onChange,
          }: {
            value: string | undefined;
            onChange: (val: string | undefined) => void;
          }) => <IconPicker value={value || ""} onChange={onChange} label="Icon" />,
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
  },
  label: "Call to Action",
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as CtaMetadata | undefined,
      windowConfig = getWindowJobsiteConfig(),
      business = puckMetadata?.business || windowConfig?.business,

      // Get phone number - check both 'phone' and 'telephone' property names
      businessPhone = business?.phone || business?.telephone,

      // Resolve the effective primary CTA via the shared precedence helper.
      finalCtaText = resolveBusinessCtaText(props.ctaText, business),
      { onePage, pagePath, pagePaths } = puckMetadata ?? {},
      // The saved ctaUrl is a generator default ("/contact"), not a choice
      // Anyone made: a "Call …" primary must actually call (hero rule).
      primary = resolvePrimaryCta({
        onePage,
        pagePath,
        pagePaths,
        phone: businessPhone,
        phoneFirst: resolvesToPhoneUrl(finalCtaText),
        savedUrl: props.ctaUrl,
        text: finalCtaText,
      }),

      // The two buttons must offer two different actions — same rule as the hero.
      // The secondary used to be the phone unconditionally, so a phone-first CTA
      // Shipped "Call Now" beside "Call (877) 806-3490", both the same tel: link.
      secondaryCta = (): { text: string; url: string } | undefined => {
        if (primary.calls) {
          return contactSecondary(pagePath, pagePaths, onePage);
        }
        if (businessPhone) {
          return { text: `Call ${formatPhone(businessPhone)}`, url: phoneHref(businessPhone) };
        }
        return undefined;
      },
      secondary = secondaryCta(),

      resolvedProps = {
        ...props,
        ctaText: primary.text,
        ctaUrl: primary.url,
        secondaryCtaText: secondary?.text,
        secondaryCtaUrl: secondary?.url,
        variant: props.variant || "band",
      };

    return <CTA id={id} {...resolvedProps} />;
  },
  /**
   * The Settings label wins over the saved page value by design; this makes the editor say
   * so instead of offering a box that silently discards what you type.
   */
  resolveFields: (_data, { fields, metadata }) =>
    withBusinessCtaOverride(fields as Record<string, unknown>, "ctaText", metadata) as typeof fields,
};

export { CTAConfig };
